import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowUpRight, MessageCircle } from "lucide-react";
import { api } from "../lib/api";

/**
 * Drip-email video landing page.
 * Renders /watch/:videoId — the email's clickable poster links here.
 * The video plays inline in a real HTML5 player (no YouTube branding,
 * no third-party redirect, no leak risk).
 */
const Watch = () => {
    const { videoId } = useParams();
    const [meta, setMeta] = useState(null);
    const [error, setError] = useState(false);

    useEffect(() => {
        api
            .get(`/drip-video/${videoId}/meta`)
            .then((r) => setMeta(r.data))
            .catch(() => setError(true));
    }, [videoId]);

    if (error) {
        return (
            <div data-testid="watch-page" className="bg-black min-h-[60vh] flex items-center justify-center px-6">
                <div className="text-center max-w-md">
                    <div className="font-stencil text-[10px] tracking-widest text-red-600">[ 404 ]</div>
                    <h1 className="mt-4 font-display text-3xl uppercase tracking-tight">Video not found.</h1>
                    <p className="mt-4 text-white/60">
                        That link may have expired. Try the Companion instead — it's built for the lie you're fighting tonight.
                    </p>
                    <Link to="/companion" className="btn-primary mt-8 inline-flex">
                        Talk to the Companion <ArrowUpRight size={18} />
                    </Link>
                </div>
            </div>
        );
    }

    if (!meta) return <div className="bg-black min-h-[60vh]" />;

    const backendUrl = process.env.REACT_APP_BACKEND_URL;

    return (
        <div data-testid="watch-page" className="bg-black">
            <section className="py-16 md:py-24 border-b border-white/10">
                <div className="max-w-4xl mx-auto px-6 lg:px-10">
                    <div className="flex items-center gap-3 mb-6 text-xs tracking-[0.4em] text-red-600 font-display uppercase">
                        <span className="h-px w-12 bg-red-600" />
                        From The Drip
                    </div>
                    <h1
                        data-testid="watch-title"
                        className="font-display text-4xl sm:text-5xl md:text-6xl uppercase tracking-tighter leading-[0.9]"
                    >
                        {meta.title}
                    </h1>
                    <p className="mt-6 text-white/75 text-lg max-w-2xl">{meta.subtitle}</p>
                </div>
            </section>

            <section className="py-12 md:py-16">
                <div className="max-w-4xl mx-auto px-6 lg:px-10">
                    <div className="relative border border-white/10 bg-[#050505] shadow-[0_0_60px_-15px_rgba(185,28,28,0.4)]">
                        <video
                            data-testid="watch-video"
                            controls
                            playsInline
                            preload="metadata"
                            poster={`${backendUrl}${meta.poster_url}`}
                            className="w-full h-auto block"
                        >
                            <source src={`${backendUrl}${meta.video_url}`} type="video/mp4" />
                            Your browser doesn't support video.
                        </video>
                    </div>

                    <div className="mt-12 grid md:grid-cols-2 gap-4">
                        <Link
                            to="/companion"
                            data-testid="watch-cta-companion"
                            className="group border border-white/10 hover:border-red-600/60 bg-[#0a0a0a] p-6 transition-colors flex items-start gap-4"
                        >
                            <MessageCircle size={22} className="text-red-600 mt-1 flex-shrink-0" />
                            <div>
                                <div className="font-display text-xl uppercase tracking-tight">
                                    Take The Lie To The Companion
                                </div>
                                <p className="mt-2 text-white/60 text-sm leading-relaxed">
                                    Tell it the repeat offender. Watch what comes back. Built for this exact moment.
                                </p>
                            </div>
                        </Link>
                        <Link
                            to="/free-tools"
                            data-testid="watch-cta-tools"
                            className="group border border-white/10 hover:border-red-600/60 bg-[#0a0a0a] p-6 transition-colors flex items-start gap-4"
                        >
                            <ArrowUpRight size={22} className="text-red-600 mt-1 flex-shrink-0" />
                            <div>
                                <div className="font-display text-xl uppercase tracking-tight">
                                    Re-Read The Battle Drill
                                </div>
                                <p className="mt-2 text-white/60 text-sm leading-relaxed">
                                    30 lies + the biblical truth that dismantles each one. Print it. Speak it.
                                </p>
                            </div>
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Watch;
